import styles from './SimpleListFromDB.module.css';
import React, {useState} from 'react';
import cn from 'classnames';
import useApi from '../../../../hooks/useApi.hook';
import {Textarea} from '../../../textarea/Textarea';
import Spinner from '../../../Spinner/Spinner';
import Button from '../../../Button/Button';
import {CustomSelect} from '../../../Select/Select';
import useTemplateRegistry from '../../../../hooks/useTemplateRegistry.hook';
import Input from '../../../Input/Input';
import {notifyDownloadQueued} from '../../../../helpers/downloadsEvents';

export function SimpleListFromDB() {
	const api = useApi();
	const baseGetItemsUrl = '/documents/get_items/';
	const baseDownloadItemsUrl = '/documents/download_items/';

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(undefined);
	const [resultRequest, setResultRequest] = useState('');

	const [searchIndex, setSearchIndex] = useState(null);

	const TEMPLATE_CODES = [
		'requiring_training_siz_list',
		'introductory_briefing_program',
		'norms_dsiz_issuance'
	];

	const {byCode: templatesByCode} = useTemplateRegistry();
	const templateOptions = TEMPLATE_CODES.map(code => ({
		value: code,
		label: templatesByCode[code]?.title ?? code
	}));

	const [selectedTemplate, setSelectedTemplate] = useState(templateOptions[0]);

	const formatIntroResponse = (exempt) => {
		if (!exempt || typeof exempt !== 'object') return '';
		const risks = Array.isArray(exempt.risks) ? exempt.risks.filter(Boolean) : [];
		const factors = Array.isArray(exempt.factors) ? exempt.factors.filter(Boolean) : [];
		return `риски: ${risks.join(', ')};\n\nфакторы: ${factors.join(', ')}`;
	};

	const formatDsizResponse = (exempt) => {
		if (!exempt || typeof exempt !== 'object') return '';
		const with_primary = Array.isArray(exempt.with_primary) ? exempt.with_primary.filter(Boolean) : [];
		const shoe_size = Array.isArray(exempt.shoe_size) ? exempt.shoe_size.filter(Boolean) : [];
		return `список должностей с первичным: ${with_primary.join(', ')};\n\nсписок должностей СИЗ ног: ${shoe_size.join(', ')}`;
	};

	const handleTemplateChange = (option) => {
		setSelectedTemplate(option);
		setResultRequest('');
		setError(undefined);
	};

	const handleRequest = async () => {
		setError(undefined);
		setLoading(true);
		const itemName = templatesByCode[selectedTemplate.value]?.item_name;

		try {
			const { data } = await api.post(
				`${baseGetItemsUrl}${itemName}/${selectedTemplate.value}`,
				{
					search_index: searchIndex,
					all_db_items: true
				}
			);

			if (data?.exempt) {
				const exempt = data.exempt;

				if (selectedTemplate.value === 'introductory_briefing_program') {
					setResultRequest(formatIntroResponse(exempt));
				} else if (selectedTemplate.value === 'norms_dsiz_issuance') {
					setResultRequest(formatDsizResponse(exempt));
				} else if (Array.isArray(exempt) && exempt.every(v => typeof v === 'string')) {
					setResultRequest(exempt.join('\n'));
				} else {
					setError('Неподдерживаемый формат данных: передайте formatExempt(exempt).');
				}
			} else {
				setError('Не удалось получить список позиций.');
			}
		} catch (e) {
			const msg = e?.response?.data?.detail || e?.message || 'Ошибка при запросе к серверу';
			setError(msg);
			setResultRequest('');
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async () => {
		setError(undefined);
		setLoading(true);
		try {
			const items_list = resultRequest.split('\n').map(s => s.trim()).filter(Boolean);
			const response = await api.post(
				`${baseDownloadItemsUrl}${selectedTemplate.value}`,
				{ items_list }
			);
			notifyDownloadQueued(response.data);
		} catch (e) {
			const msg = e?.response?.data?.detail || e?.message || 'Ошибка при запросе к серверу';
			setError(msg);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<div className={styles.container}>
				{error && (
					<div className={styles.error} onClick={() => setError(undefined)}>
						{error} <span className={styles.errorClose}>✖</span>
					</div>
				)}
				<h1 className={styles.title}>Генератор списочных документов</h1>
				<CustomSelect
					className={styles.my_wider_select}
					value={templateOptions.find(o => o.value === selectedTemplate?.value) || null}
					options={templateOptions}
					placeholder="Тип документа"
					onChange={handleTemplateChange}
					width="100%"
				/>
				<Input
					placeholder="Поисковый индекс"
					value={searchIndex}
					onChange={(e) => setSearchIndex(e.target.value)}
				/>
				<div className={styles['button']}>
					<Button onClick={handleRequest} disabled={loading}>
						{loading ? 'Запрос в обработке...' : 'Сформировать'}
					</Button>
				</div>
				<div className={styles['span']}>
					<div className={styles['textarea-container']}>
						<Textarea
							text={resultRequest}
							setText={setResultRequest}
							placeholder="Результат будет показан здесь"
							disabled={false}
							className={styles['textarea']}
						/>
						{loading && (
							<div className={styles['spinner-overlay']}>
								<Spinner showSeconds={true} />
							</div>
						)}
					</div>
				</div>
				<div className={styles['button']}>
					<Button
						onClick={handleDownload}
						disabled={!resultRequest}
						className={cn({ [styles.disabled]: !resultRequest })}
					>
						Выгрузить в файл
					</Button>
				</div>
			</div>
		</>
	);
}
